/**
 * SideScrollerMode
 *
 * 2D side-scroller mode with Z-band freedom.
 * Player moves forward/backward along spline AND up/down within a Z-band.
 * Uses soft spring constraint at band edges.
 */

import { Vector3 } from '@babylonjs/core';
import type { SplineConstraint, SplineProjection } from '../SplineConstraint';
import type { IGameplayMode } from './IGameplayMode';
import type {
  MovementInput,
  MovementResult,
  ConstraintResult,
  GameplayModeContext
} from '../../types/gameplay';

/** Default Z-band configuration */
const DEFAULT_Z_BAND_WIDTH = 5.0; // meters from spline center
const DEFAULT_Z_BAND_SPRING_K = 50.0; // N/m spring constant

export class SideScrollerMode implements IGameplayMode {
  readonly id = 'side_scroller' as const;
  readonly displayName = 'Side Scroller';
  readonly allowsInput = true;
  readonly usesVerticalInput = true;

  updateMovement(
    input: MovementInput,
    ctx: GameplayModeContext,
    _deltaTime: number
  ): MovementResult {
    const { path, velocity, moveSpeed } = ctx;
    const spline = path.constraint;

    // Project current position to get tangent and binormal
    const { projection } = spline.project(ctx.position, ctx.lastClosestIndex);
    const tangent = projection.tangent.normalize();
    const binormal = projection.binormal.normalize();

    // Calculate desired velocity
    const desiredVelocity = new Vector3(0, velocity.y, 0);

    // Horizontal = along tangent (forward/back)
    const horizontalMag = Math.abs(input.horizontal);
    if (horizontalMag > 0.01) {
      const hDir = Math.sign(input.horizontal);
      desiredVelocity.x += tangent.x * horizontalMag * moveSpeed * hDir;
      desiredVelocity.z += tangent.z * horizontalMag * moveSpeed * hDir;
    }

    // Vertical = along binormal (into/out of screen within Z-band)
    const verticalMag = Math.abs(input.vertical);
    if (verticalMag > 0.01) {
      const vDir = Math.sign(input.vertical);
      desiredVelocity.x += binormal.x * verticalMag * moveSpeed * vDir;
      desiredVelocity.z += binormal.z * verticalMag * moveSpeed * vDir;
    }

    // Determine facing direction (only from horizontal input)
    const facingDirection = horizontalMag > 0.01 ? Math.sign(input.horizontal) : 0;

    return {
      velocity: desiredVelocity,
      facingDirection
    };
  }

  constrainPosition(
    position: Vector3,
    ctx: GameplayModeContext,
    projection: SplineProjection
  ): ConstraintResult {
    const { path } = ctx;
    const zBandWidth = path.config.zBandWidth ?? DEFAULT_Z_BAND_WIDTH;
    const springK = path.config.zBandSpringK ?? DEFAULT_Z_BAND_SPRING_K;

    // Calculate distance from spline along binormal
    const toPosition = position.subtract(projection.splinePoint);
    const binormalDist = Vector3.Dot(toPosition, projection.binormal);

    let pendingForce: Vector3 | undefined;

    // Apply soft spring constraint at band edges
    if (Math.abs(binormalDist) > zBandWidth) {
      const overshoot = Math.abs(binormalDist) - zBandWidth;
      const springForce = overshoot * springK;
      // Force pushes back toward band center
      pendingForce = projection.binormal.scale(-springForce * Math.sign(binormalDist));
    }

    // Don't snap to spline - allow Z freedom within band
    // Only preserve Y from physics
    return {
      position: position.clone(),
      closestIndex: ctx.lastClosestIndex,
      pendingForce
    };
  }

  getTargetRotation(
    movementDirection: number,
    spline: SplineConstraint,
    t: number
  ): number {
    // Get tangent at current T
    const point = spline.getPointAt(t);
    const { projection } = spline.project(point, 0);
    const tangent = projection.tangent;

    // Face along tangent in movement direction
    const facingTangent = movementDirection >= 0 ? tangent : tangent.negate();
    return Math.atan2(facingTangent.x, facingTangent.z);
  }
}
